/* eslint-disable */

import { ls } from '../LocalStorage';
import Env from '../env';
import Authorization from '../Authorization';

let refreshPromise = null;
let refreshVersion = 0;
let xsrfBootstrapPromise = null;

/**
 * All normal API requests currently in progress.
 *
 * IMPORTANT:
 * Refresh request itself is NOT added here.
 */
const inFlightRequests = new Set();

/**
 * Maximum amount of time refresh waits for already-running
 * API requests before continuing.
 */
const IN_FLIGHT_WAIT_TIMEOUT = 5000;

const STATE_CHANGING_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

let isLoggingOut = false;

/**
 * 
 * LOGOUT
 *
 * Prevent multiple logout operations.
 *
 * This blocks NEW API requests after logout starts.
 * Requests that were already physically sent are not cancelled.
 */
export const logoutOnce = () => {
  if (isLoggingOut) {
    return;
  }

  isLoggingOut = true;

  console.warn('[SessionManager] LOGOUT');

  Authorization.logout();
};

/**
 * Reset shared session state after successful login.
 *
 * IMPORTANT:
 * logoutOnce() sets isLoggingOut = true.
 * Therefore, after a successful login, this should be called.
 */
export const resetSessionState = () => {
  isLoggingOut = false;

  console.log('[SessionManager] SESSION STATE RESET');
};

/**
 * 
 * XSRF TOKEN
 * 
 */

export const getXsrfToken = () => {
  if (typeof document === 'undefined') {
    return null;
  }

  const xsrfCookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith('XSRF-TOKEN='));

  if (!xsrfCookie) {
    return null;
  }

  try {
    return decodeURIComponent(xsrfCookie.substring('XSRF-TOKEN='.length));
  } catch (error) {
    console.warn('[SessionManager] Unable to decode XSRF cookie');

    return null;
  }
};

/**
 * 
 * TRACK IN-FLIGHT REQUEST
 *
 * Tracks normal API requests.
 *
 * Refresh request is deliberately NOT tracked.
 */
const trackRequest = (requestPromise) => {
  inFlightRequests.add(requestPromise);

  requestPromise.finally(() => {
    inFlightRequests.delete(requestPromise);
  });

  return requestPromise;
};

/**
 * 
 * WAIT FOR IN-FLIGHT REQUESTS
 *
 * When refresh starts, we wait for the requests that were
 * already running at that moment.
 *
 * This allows a newer API request to establish/update the
 * latest XSRF cookie before refresh reads it.
 *
 * BUT:
 *
 * We never wait forever.
 *
 * Maximum wait = 5 seconds.
 */
const waitForInFlightRequests = async () => {
  if (inFlightRequests.size === 0) {
    return;
  }

  /**
   * Take a snapshot.
   *
   * New requests started after refresh begins are NOT included.
   */
  const requestsAtRefreshStart = Array.from(inFlightRequests);

  console.log('[SessionManager] REFRESH WAITING FOR IN-FLIGHT REQUESTS', {
    count: requestsAtRefreshStart.length,
  });

  let timeoutId;

  const requestsCompleted = Promise.allSettled(requestsAtRefreshStart).then(
    () => 'completed'
  );

  const timeoutPromise = new Promise((resolve) => {
    timeoutId = setTimeout(() => {
      console.warn(
        '[SessionManager] IN-FLIGHT WAIT TIMEOUT - CONTINUING REFRESH',
        {
          count: requestsAtRefreshStart.length,
          timeout: IN_FLIGHT_WAIT_TIMEOUT,
        }
      );

      resolve('timeout');
    }, IN_FLIGHT_WAIT_TIMEOUT);
  });

  const result = await Promise.race([requestsCompleted, timeoutPromise]);

  clearTimeout(timeoutId);

  console.log('[SessionManager] IN-FLIGHT REQUEST WAIT FINISHED', {
    result,
  });
};

/**
 * 
 * FETCH WITH XSRF
 *
 * Responsibilities:
 *
 * 1. Block new API calls after logout.
 * 2. Wait if a session refresh is already running.
 * 3. Handle initial XSRF bootstrap.
 * 4. Track normal API requests.
 */
export const fetchWithXsrf = async (sendRequest) => {
  /**
   * 
   * LOGOUT CHECK
   * 
   */
  if (isLoggingOut) {
    console.warn('[SessionManager] API BLOCKED - LOGOUT IN PROGRESS');

    throw new Error('SESSION_LOGOUT_IN_PROGRESS');
  }

  /**
   * 
   * REFRESH CHECK
   *
   * If another API is already refreshing the session,
   * wait for that refresh before sending a new API request.
   */
  if (refreshPromise) {
    console.log(
      '[SessionManager] WAITING FOR ACTIVE REFRESH BEFORE API REQUEST'
    );

    const refreshResult = await refreshPromise;

    if (!refreshResult?.ok) {
      throw refreshResult?.error || new Error('SESSION_REFRESH_FAILED');
    }
  }

  /**
   * Read the latest XSRF token after waiting for refresh.
   */
  const existingToken = getXsrfToken();

  /**
   * 
   * NORMAL REQUEST - TOKEN EXISTS
   * 
   */
  if (existingToken) {
    const requestPromise = Promise.resolve(sendRequest(existingToken));

    return trackRequest(requestPromise);
  }

  /**
   * 
   * NO XSRF TOKEN
   *
   * First request becomes the bootstrap request.
   */
  if (!xsrfBootstrapPromise) {
    console.warn('[SessionManager] NO XSRF TOKEN - STARTING BOOTSTRAP');

    const bootstrapRequest = Promise.resolve().then(() => sendRequest(null));

    const trackedBootstrapRequest = trackRequest(bootstrapRequest);

    xsrfBootstrapPromise = trackedBootstrapRequest;

    try {
      return await trackedBootstrapRequest;
    } finally {
      xsrfBootstrapPromise = null;

      console.log('[SessionManager] XSRF BOOTSTRAP LOCK RELEASED');
    }
  }

  /**
   * 
   * ANOTHER REQUEST IS ALREADY BOOTSTRAPPING XSRF
   * 
   */
  console.log('[SessionManager] WAITING FOR XSRF BOOTSTRAP');

  try {
    await xsrfBootstrapPromise;
  } catch (error) {
    console.warn('[SessionManager] XSRF BOOTSTRAP FAILED:', error);
  }

  /**
   * Read latest token after bootstrap.
   */
  const latestXsrfToken = getXsrfToken();

  const requestPromise = Promise.resolve(sendRequest(latestXsrfToken));

  return trackRequest(requestPromise);
};

/**
 * 
 * UPDATE EXPIRATION
 * 
 */

const updateExpiration = async (response) => {
  try {
    const json = await response
      .clone()
      .json()
      .catch(() => null);

    if (json && json.tokenExpirationTime) {
      ls.removeItem('expires_on');

      ls.setItem('expires_on', String(json.tokenExpirationTime));
    }
  } catch (error) {
    /**
     * Refresh already succeeded.
     *
     * Ignore response parsing errors.
     */
  }
};

/**
 * 
 * PERFORM REFRESH REQUEST
 *
 * IMPORTANT:
 * This request is NOT added to inFlightRequests.
 *
 * Otherwise refresh would wait for itself.
 */
const performRefreshRequest = async (attempt, xsrfTokenOverride = null) => {
  try {
    const baseUrl = Env.getEnv('REACT_APP_BACKEND_BASE_URL_V3');

    const refreshUrl = `${baseUrl}/oauth2/refresh`;

    /**
     * Always use the latest available XSRF token.
     */
    const xsrfToken = xsrfTokenOverride || getXsrfToken();

    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    if (xsrfToken) {
      headers['X-XSRF-TOKEN'] = xsrfToken;
    }

    console.log(`[SessionManager] REFRESH #${attempt} START`, {
      url: refreshUrl,
      xsrfAvailable: Boolean(xsrfToken),
    });

    const response = await fetch(refreshUrl, {
      method: 'POST',
      credentials: 'include',
      headers,
    });

    console.log(`[SessionManager] REFRESH #${attempt} RESPONSE`, {
      status: response.status,
      ok: response.ok,
    });

    /**
     * --------------------------------------------------------
     * REFRESH SUCCESS
     * --------------------------------------------------------
     */
    if (response.ok) {
      await updateExpiration(response);

      /**
       * Increment shared refresh version.
       */
      refreshVersion += 1;

      console.log(`[SessionManager] REFRESH #${attempt} SUCCESS`, {
        refreshVersion,
      });

      return {
        ok: true,
        response,
      };
    }

    /**
     * 
     * REFRESH FAILED
     * 
     */
    const responseText = await response
      .clone()
      .text()
      .catch(() => '');

    console.warn(`[SessionManager] REFRESH #${attempt} FAILED`, {
      status: response.status,
      body: responseText,
    });

    return {
      ok: false,
      response,
      status: response.status,
      error: new Error(`Session refresh failed with status ${response.status}`),
    };
  } catch (error) {
    console.error(`[SessionManager] REFRESH #${attempt} EXCEPTION`, error);

    return {
      ok: false,
      status: 0,
      error,
    };
  }
};

/**
 * 
 * REFRESH WITH ONE RETRY
 * 
 * Flow:
 *
 * 401
 *  ↓
 * Wait for existing API requests
 *  ↓
 * Read latest XSRF
 *  ↓
 * Refresh #1
 *  ↓
 * SUCCESS → STOP
 *
 * FAILURE
 *  ↓
 * Read latest XSRF again
 *  ↓
 * Refresh #2
 *  ↓
 * STOP
 */
const performRefreshWithRetry = async () => {
  /**
   * IMPORTANT:
   *
   * We do NOT separately wait forever for the XSRF bootstrap.
   *
   * The bootstrap request is already tracked in
   * inFlightRequests.
   *
   * Therefore it is covered by the same 5-second limit.
   *
   * This prevents a bootstrap 401 from causing a deadlock.
   */
  await waitForInFlightRequests();

  /**
   * Read latest token after existing requests have settled
   * or the 5-second timeout has been reached.
   */
  const latestXsrfToken = getXsrfToken();

  console.log('[SessionManager] USING LATEST XSRF BEFORE REFRESH', {
    xsrfAvailable: Boolean(latestXsrfToken),
  });

  /**
   * 
   * REFRESH #1
   * 
   */
  const firstResult = await performRefreshRequest(1, latestXsrfToken);

  /**
   * IMPORTANT:
   *
   * If refresh #1 succeeds, STOP.
   *
   * No second refresh.
   */
  if (firstResult.ok) {
    console.log('[SessionManager] REFRESH #1 SUCCESS - STOPPING');

    return firstResult;
  }

  /**
   * 
   * REFRESH #2
   * 
   *
   * Refresh #1 failed.
   *
   * Another API may have updated the XSRF token while
   * refresh #1 was running.
   *
   * Therefore read the cookie again.
   */
  const retryXsrfToken = getXsrfToken();

  console.warn('[SessionManager] REFRESH #1 FAILED - RETRYING ONCE', {
    xsrfAvailable: Boolean(retryXsrfToken),
  });

  const secondResult = await performRefreshRequest(2, retryXsrfToken);

  return secondResult;
};

/**
 * 
 * REFRESH VERSION
 * 
 */

export const getRefreshVersion = () => refreshVersion;

/**
 * Prevent delayed 401 responses from starting another refresh
 * if another request already refreshed the session.
 */
export const refreshSessionIfNeeded = async (requestRefreshVersion) => {
  if (requestRefreshVersion !== refreshVersion) {
    console.log(
      '[SessionManager] REFRESH ALREADY COMPLETED FOR THIS REQUEST - SKIPPING',
      {
        requestRefreshVersion,
        currentRefreshVersion: refreshVersion,
      }
    );

    return {
      ok: true,
      alreadyRefreshed: true,
    };
  }

  return refreshSession();
};

/**
 * 
 * SHARED REFRESH
 * 
 *
 * This is the ONLY refresh owner.
 *
 * RequestFactory and Api.js both use this.
 */
export const refreshSession = async () => {
  /**
   * Another refresh is already running.
   *
   * Everyone receives the same Promise.
   */
  if (refreshPromise) {
    console.log('[SessionManager] REFRESH ALREADY IN PROGRESS - WAITING');

    return refreshPromise;
  }

  console.warn('[SessionManager] CREATING SHARED REFRESH PROMISE');

  /**
   * Assign immediately.
   *
   * This is important because concurrent 401 handlers
   * must see the same Promise.
   */
  refreshPromise = performRefreshWithRetry();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;

    console.log('[SessionManager] SHARED REFRESH PROMISE CLEARED');
  }
};

/**
 * 
 * METHOD HELPER
 * 
 */

export const isStateChangingMethod = (method = 'GET') =>
  STATE_CHANGING_METHODS.includes(method.toUpperCase());
